/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {FC, useCallback, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core/styles';
import {AnalyticsIds} from '@ion/core/src/analytics/types';
import {translate} from '@ion/core/src/locale/locale';
import AvatarButton from '@ion/dls/src/components/buttons/AvatarButton';
import {CTALink} from '@ion/dls/src/components/buttons/CTALink';
import {HelpCenterButton} from '@ion/dls/src/components/buttons/HelpCenterButton';
import NotificationButton from '@ion/dls/src/components/buttons/NotificationButton';
import Switch from '@ion/dls/src/components/switch/Switch';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import Render from '@ion/dls/src/render/Render';
import {showHelpSection} from '@ion/dls/src/theme/resellers/Resellers';
import useReseller from '@ion/dls/src/theme/resellers/useReseller';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import TryNewThemeSwitch from '@ion/dls/src/theme/TryNewThemeSwitch';
import useThemeContext from '@ion/dls/src/theme/useThemeContext';
import {
  hasAppGroupChooserAccess,
  hasAppHelpAccess,
  hasAppNotificationAccess
} from '@/security/Features';
import {hasNewThemeToggleAccess} from '@/security/themeAccess';
import {setNotificationsPanelStatus} from '@/store/actions/notifications';
import {getIONPrimeURL} from '@/store/helpers';
import {isFromPrimeSelector} from '@/store/selectors/initialization-selectors';
import {
  previewFeatureSelector,
  usernameSelector
} from '@/store/selectors/login-selectors';
import {isPanelOpenSelector, unreadCountSelector} from '@/store/selectors/notifications-selectors';
import DesktopUserProfileView from './DesktopUserProfileView';
import GlobalGroupChooserDrawer from './GlobalGroupChooserDrawer';
import styles from './DesktopHeader.module.scss';

export interface DesktopHeaderNotificationProps {
  onContactSupportOpen: () => void;
  isAccountRestricted: boolean;
}
export interface DesktopHeaderNotificationTypes {
  onHelp?: Function;
  onChat?: Function;
  onNotification?: Function;
  onUserProfile?: Function;
  username?: string;
  onLogout?: () => void;
  onViewIONPrime?: Function;
  hasPreviewFeature?: boolean;
  isLoggingOut?: boolean;
  notificationCount?: number;
  isSelected?: boolean;
};

const useStyles = makeStyles(theme => ({
  ionLink : {
    marginRight : '8px',
    ...dlsAlt('components', 'ionToggle', 'ionBeta')
  },
  betaLink : {
    backgroundColor : dlsColor('caBlue'),
    borderRadius    : '10px',
    ...dlsAlt('components', 'ionpreviewText', 'new')
  },
  toggleLink : {
    marginRight : '65px',
    '& > *'     : {
      padding : '0 5px'
    }
  },
  supLink : {
    position : 'relative',
    top      : '-0.5em',
    left     : '0.8em'
  }
}));

export const toggleTooltip = makeStyles((theme) => ({
  arrow : {
    color : dlsColor('grey1')
  },
  tooltip : {
    ...dlsAlt('components', 'tooltip')
  }
}));

const ToggleTooltip = function(props) {
  return <Tooltip arrow {...props} />;
};

export const IconCustomTooltip = function(props) {
  return <Tooltip arrow {...props} />;
};

/**
 * @DesktopHeaderNotification
 *
 * The Desktop Header Notification component provides the application Header
 * Notification for the iOn Application
 * when view in Desktop Mode.
 */

export const DesktopHeaderNotification: FC<DesktopHeaderNotificationProps> = ({
  onContactSupportOpen,
  isAccountRestricted
}) => {
  const canAccessNotification        = hasAppNotificationAccess();
  const canAccessHelp                = hasAppHelpAccess();
  const canAccessGroupChooser        = hasAppGroupChooserAccess();
  const canSwitchTheme               = hasNewThemeToggleAccess();
  const {isCurrent: isCurrentTheme}  = useThemeContext();
  const {isReseller}                 = useReseller();
  const dispatch                     = useDispatch();
  const [toggle, setToggle]   = useState(false);
  const [
    isUserProfileView,
    setIsUserProfileView
  ]                         = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const content   = translate('nav:ionprime.toolTipContent');
  const trademark = translate('nav:ionprime.trademark');
  const message   = translate('nav:ionprime.toolTipSubContent');
  const [isMouseClick, setMouseClick] = useState(false);
  const notificationCount     = useSelector(unreadCountSelector);
  const classes               = useStyles({notificationCount});
  const notificationPanelOpen = useSelector(isPanelOpenSelector);
  const username              = useSelector(usernameSelector);
  const hasPreviewFeature     = useSelector(previewFeatureSelector);
  const isFromPrime           = useSelector(isFromPrimeSelector);

  function handleClick(event: React.MouseEvent<HTMLElement>) {
    setAnchorEl(anchorEl ? null : event.currentTarget);
    setIsUserProfileView(!isUserProfileView);
  };

  function handleMouseDown() {
    setMouseClick(true);
  }

  function handleKeyDown() {
    setMouseClick(false);
  }

  const toggleNotificationPanel = useCallback(function() {
    dispatch(setNotificationsPanelStatus(!notificationPanelOpen));
  }, [notificationPanelOpen]);

  const onViewIONPrime = useCallback(function() {
    setToggle(!toggle);
    window.open(getIONPrimeURL(window.location), '_self');
    if (!isFromPrime) {
      window.close();
    }
  }, [toggle]);

  const open = Boolean(anchorEl) && isUserProfileView;
  const id = open ? 'simple-popover' : undefined;

  return (
    <div className={isMouseClick ? styles.iconsAlt : styles.icons} style={{flexGrow: 1}}>
      <Render.If condition={hasPreviewFeature}>
        <div id='bottom' className={classes.toggleLink}>
          <div style={{whiteSpace: 'nowrap'}}>
            <ToggleTooltip title={
              (<div>{content}<sup style={{textTransform: 'uppercase', fontSize: 10}}>{trademark}</sup>&ensp;{message}</div>)
            }>
              <span className={classes.supLink}>
                <sup className={classes.betaLink}>{translate('nav:ionprime.title')}</sup>
              </span>
            </ToggleTooltip>
            <CTALink className={classes.ionLink}>{translate('nav:ionprime.preview')}</CTALink>
          </div>
          <div>
            <Switch marginRight='56px' onChange={function() {
              onViewIONPrime();
            }} checked />
          </div>
        </div>
      </Render.If>
      {/*
        [ION-APP TODO]: Restore message icon when chat is available
        See the following link for the code to be restored:
        https://bitbucket.org/dl-itendusersupport/ion-monorepo/src/master/packages/ion-suite/src/components/header/DesktopHeaderNotification.tsx?at=v.ion-suite-3.0.7#184
      */}
      {/* the legacy theme's way into the current theme, just before the
          notifications - only with the `dls` Feature Flag (?features=dls),
          and never under a reseller's own theme ... */}
      <Render.If condition={!isCurrentTheme && !isReseller && canSwitchTheme}>
        <TryNewThemeSwitch />
      </Render.If>
      <Render.If condition={!isAccountRestricted && canAccessNotification}>
        <NotificationButton notificationCount={notificationCount}
          toggleNotificationPanel={toggleNotificationPanel} />
      </Render.If>
      <Render.If condition={showHelpSection() && canAccessHelp}>
        <HelpCenterButton elementId={AnalyticsIds.helpCenterIcon} />
      </Render.If>
      <Render.If condition={!isCurrentTheme && canAccessGroupChooser}>
        <GlobalGroupChooserDrawer />
      </Render.If>
      <AvatarButton username={username} handleClick={handleClick} />
      <Render.If condition={isUserProfileView}>
        <DesktopUserProfileView open={open}
          id={id}
          onContactSupportOpen={onContactSupportOpen}
          anchorEl={anchorEl}
          handleClick={handleClick}
          username={username} />
      </Render.If>
    </div>
  );
};

export default React.memo(DesktopHeaderNotification);
